export const KCM1 = Object.freeze({ headerBytes: 64, tagBytes: 16, version: 1, minLog2: 12, maxLog2: 20, maxObjectBytes: 300_000_000 });

export class Kcm1Error extends Error {
  constructor(code) {
    super(code);
    this.name = "Kcm1Error";
    this.code = code;
  }
}

const U32_MAX = 0xffffffff;
const SHA256_HEX = /^[0-9a-f]{64}$/;
const BASE64 = /^[A-Za-z0-9+/]*={0,2}$/;
const KDF_LABEL = new TextEncoder().encode("kinlo/media-chunked/v1\u0000");
const subtle = () => globalThis.crypto.subtle;

const isLog2 = (value) => Number.isInteger(value) && value >= KCM1.minLog2 && value <= KCM1.maxLog2;
const isByteCount = (value) => Number.isSafeInteger(value) && value >= 0;
const isNonEmptyString = (value) => typeof value === "string" && value.length > 0;
const isBytes = (value, length) => value instanceof Uint8Array && value.byteLength === length;

function decodeBase64(text, length) {
  if (typeof text !== "string" || text.length % 4 !== 0 || !BASE64.test(text)) return null;
  let binary;
  try {
    binary = atob(text);
  } catch {
    return null;
  }
  if (binary.length !== length || btoa(binary) !== text) return null;
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

function toHex(bytes) {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function sameBytes(a, b) {
  if (a.byteLength !== b.byteLength) return false;
  for (let i = 0; i < a.byteLength; i++) if (a[i] !== b[i]) return false;
  return true;
}

function plainLength(desc, index) {
  const size = 2 ** desc.chunkSizeLog2;
  return Math.min(size, desc.plaintextBytes - index * size);
}

async function sha256Hex(bytes) {
  return toHex(new Uint8Array(await subtle().digest("SHA-256", bytes)));
}

export function chunkCount(plaintextBytes, log2) {
  if (!isByteCount(plaintextBytes) || !isLog2(log2)) throw new RangeError("kcm1: invalid length or chunk size");
  return Math.max(1, Math.ceil(plaintextBytes / 2 ** log2));
}

export function ciphertextLength(plaintextBytes, log2) {
  const length = KCM1.headerBytes + plaintextBytes + KCM1.tagBytes * chunkCount(plaintextBytes, log2);
  if (!Number.isSafeInteger(length)) throw new RangeError("kcm1: ciphertext length is not a safe integer");
  return length;
}

export function maxPlaintextBytes(log2, maxObjectBytes = KCM1.maxObjectBytes) {
  if (!isLog2(log2) || !Number.isSafeInteger(maxObjectBytes) || maxObjectBytes < KCM1.headerBytes + KCM1.tagBytes) {
    throw new RangeError("kcm1: invalid chunk size or object bound");
  }
  let low = 0;
  let high = maxObjectBytes - KCM1.headerBytes - KCM1.tagBytes;
  while (low < high) {
    const mid = low + Math.ceil((high - low) / 2);
    if (ciphertextLength(mid, log2) <= maxObjectBytes) low = mid;
    else high = mid - 1;
  }
  return low;
}

export function objectAad({ familyId, mediaId, kind, r2Key }) {
  if ((kind !== "full" && kind !== "doc") || !isNonEmptyString(familyId) || !isNonEmptyString(mediaId) || !isNonEmptyString(r2Key)) {
    throw new Kcm1Error("aad");
  }
  return `family:${familyId}|media:${mediaId}|kind:${kind}|r2Key:${r2Key}|format:kcm1|schema:2`;
}

export function buildHeader({ plaintextBytes, chunkSizeLog2, salt }) {
  if (!isBytes(salt, 32) || !isByteCount(plaintextBytes) || !isLog2(chunkSizeLog2)) throw new Kcm1Error("envelope");
  const count = chunkCount(plaintextBytes, chunkSizeLog2);
  if (count > U32_MAX) throw new Kcm1Error("envelope");
  const header = new Uint8Array(KCM1.headerBytes);
  const view = new DataView(header.buffer);
  header.set([0x4b, 0x43, 0x4d, 0x31], 0);
  header[4] = KCM1.version;
  header[5] = chunkSizeLog2;
  view.setUint32(8, Math.floor(plaintextBytes / 2 ** 32));
  view.setUint32(12, plaintextBytes % 2 ** 32);
  view.setUint32(16, count);
  header.set(salt, 32);
  return header;
}

function parseKcm1(bucket, familyId, mediaId, kind) {
  const aad = objectAad({ familyId, mediaId, kind, r2Key: bucket.r2Key });
  if (bucket.aad !== aad || !isNonEmptyString(bucket.url) || typeof bucket.contentSha256 !== "string" || !SHA256_HEX.test(bucket.contentSha256)) return null;
  const { plaintextBytes, ciphertextBytes, chunkSizeLog2, chunkCount: count } = bucket;
  if (!isLog2(chunkSizeLog2) || !isByteCount(plaintextBytes) || !isByteCount(ciphertextBytes) || !isByteCount(count)) return null;
  if (!(ciphertextBytes <= KCM1.maxObjectBytes) || plaintextBytes > ciphertextBytes) return null;
  if (count !== chunkCount(plaintextBytes, chunkSizeLog2) || ciphertextBytes !== ciphertextLength(plaintextBytes, chunkSizeLog2)) return null;
  const salt = decodeBase64(bucket.saltB64, 32);
  if (!salt) return null;
  return Object.freeze({
    format: "kcm1",
    kind,
    aad,
    r2Key: bucket.r2Key,
    url: bucket.url,
    contentSha256: bucket.contentSha256,
    plaintextBytes,
    ciphertextBytes,
    chunkSizeLog2,
    chunkCount: count,
    saltB64: bucket.saltB64,
    salt,
    header: buildHeader({ plaintextBytes, chunkSizeLog2, salt }),
  });
}

function parseGcm(bucket, familyId, mediaId, kind) {
  if (!["full", "doc", "thumb"].includes(kind) || !isNonEmptyString(familyId) || !isNonEmptyString(mediaId)) return null;
  if (!isNonEmptyString(bucket.r2Key) || !isNonEmptyString(bucket.url) || typeof bucket.contentSha256 !== "string" || !SHA256_HEX.test(bucket.contentSha256)) return null;
  const aad = `family:${familyId}|media:${mediaId}|kind:${kind}|r2Key:${bucket.r2Key}|sha256:${bucket.contentSha256}|schema:1`;
  if (bucket.aad !== aad) return null;
  const { plaintextBytes, ciphertextBytes } = bucket;
  if (!isByteCount(plaintextBytes) || ciphertextBytes !== plaintextBytes || !(ciphertextBytes <= KCM1.maxObjectBytes)) return null;
  const nonceBytes = decodeBase64(bucket.nonceB64, 12);
  const tag = decodeBase64(bucket.tagB64, 16);
  if (!nonceBytes || !tag) return null;
  return Object.freeze({
    format: "gcm",
    kind,
    aad,
    r2Key: bucket.r2Key,
    url: bucket.url,
    contentSha256: bucket.contentSha256,
    plaintextBytes,
    ciphertextBytes,
    nonce: nonceBytes,
    tag,
  });
}

export function parseDescriptor(bucket, { familyId, mediaId, kind } = {}) {
  try {
    if (typeof bucket !== "object" || bucket === null || Array.isArray(bucket)) return null;
    if (bucket.format === "kcm1") return parseKcm1(bucket, familyId, mediaId, kind);
    if (bucket.format === "gcm") return parseGcm(bucket, familyId, mediaId, kind);
    return null;
  } catch {
    return null;
  }
}

export async function importDek(dekBytes) {
  if (!(dekBytes instanceof Uint8Array)) throw new Kcm1Error("envelope");
  try {
    if (dekBytes.byteLength !== 32) throw new Kcm1Error("envelope");
    const hkdfKey = await subtle().importKey("raw", dekBytes, "HKDF", false, ["deriveKey"]);
    const gcmKey = await subtle().importKey("raw", dekBytes, { name: "AES-GCM" }, false, ["decrypt"]);
    return { hkdfKey, gcmKey };
  } finally {
    dekBytes.fill(0);
  }
}

export async function deriveChunkKey(hkdfKey, desc, { extractable = false } = {}) {
  if (desc?.format !== "kcm1") throw new Kcm1Error("envelope");
  const aad = new TextEncoder().encode(desc.aad);
  const info = new Uint8Array(KDF_LABEL.byteLength + aad.byteLength);
  info.set(KDF_LABEL, 0);
  info.set(aad, KDF_LABEL.byteLength);
  return subtle().deriveKey({ name: "HKDF", hash: "SHA-256", salt: desc.salt, info }, hkdfKey, { name: "AES-GCM", length: 256 }, extractable, ["decrypt"]);
}

export function nonce(index) {
  if (!Number.isInteger(index) || index < 0 || index > U32_MAX) throw new RangeError("kcm1: invalid chunk index");
  const bytes = new Uint8Array(12);
  new DataView(bytes.buffer).setUint32(8, index);
  return bytes;
}

export function chunkAad(header, index, count) {
  if (!isBytes(header, KCM1.headerBytes) || !Number.isInteger(count) || count < 1 || count > U32_MAX || !Number.isInteger(index) || index < 0 || index >= count) {
    throw new RangeError("kcm1: invalid chunk AAD input");
  }
  const aad = new Uint8Array(KCM1.headerBytes + 9);
  const view = new DataView(aad.buffer);
  aad.set(header, 0);
  view.setUint32(64, index);
  view.setUint32(68, count);
  aad[72] = index === count - 1 ? 1 : 0;
  return aad;
}

export function chunkCipherLength(desc, index) {
  if (desc?.format !== "kcm1") throw new Kcm1Error("envelope");
  if (!Number.isInteger(index) || index < 0 || index >= desc.chunkCount) throw new RangeError("kcm1: invalid chunk index");
  return plainLength(desc, index) + KCM1.tagBytes;
}

export function planRange(desc, start, endInclusive) {
  const count = chunkCount(desc.plaintextBytes, desc.chunkSizeLog2);
  if (desc.chunkCount !== count || !Number.isInteger(start) || !Number.isInteger(endInclusive) || start < 0 || start > endInclusive || endInclusive >= desc.plaintextBytes) {
    throw new RangeError("kcm1: invalid range");
  }
  const size = 2 ** desc.chunkSizeLog2;
  const firstChunk = Math.floor(start / size);
  const lastChunk = Math.floor(endInclusive / size);
  return {
    firstChunk,
    lastChunk,
    cipherStart: KCM1.headerBytes + firstChunk * (size + KCM1.tagBytes),
    cipherEndInclusive: KCM1.headerBytes + lastChunk * (size + KCM1.tagBytes) + plainLength(desc, lastChunk) + KCM1.tagBytes - 1,
  };
}

export async function decryptChunk(chunkKey, header, index, count, cipherWithTag) {
  const iv = nonce(index);
  const additionalData = chunkAad(header, index, count);
  try {
    return new Uint8Array(await subtle().decrypt({ name: "AES-GCM", iv, additionalData, tagLength: 128 }, chunkKey, cipherWithTag));
  } catch {
    throw new Kcm1Error("chunk");
  }
}

export async function decryptKcm1Object(objectBytes, chunkKey, desc) {
  if (desc?.format !== "kcm1") throw new Kcm1Error("envelope");
  if (!(objectBytes instanceof Uint8Array) || objectBytes.byteLength !== desc.ciphertextBytes) throw new Kcm1Error("length");
  if (!sameBytes(objectBytes.subarray(0, KCM1.headerBytes), desc.header)) throw new Kcm1Error("header");
  const plaintext = new Uint8Array(desc.plaintextBytes);
  let offset = KCM1.headerBytes;
  for (let index = 0; index < desc.chunkCount; index++) {
    const length = chunkCipherLength(desc, index);
    plaintext.set(await decryptChunk(chunkKey, desc.header, index, desc.chunkCount, objectBytes.subarray(offset, offset + length)), index * 2 ** desc.chunkSizeLog2);
    offset += length;
  }
  if ((await sha256Hex(plaintext)) !== desc.contentSha256) throw new Kcm1Error("digest");
  return plaintext;
}

export async function decryptGcmObject(cipherBytes, gcmKey, desc) {
  if (desc?.format !== "gcm") throw new Kcm1Error("envelope");
  if (!(cipherBytes instanceof Uint8Array) || cipherBytes.byteLength !== desc.ciphertextBytes) throw new Kcm1Error("length");
  const sealed = new Uint8Array(cipherBytes.byteLength + KCM1.tagBytes);
  sealed.set(cipherBytes, 0);
  sealed.set(desc.tag, cipherBytes.byteLength);
  let plaintext;
  try {
    plaintext = new Uint8Array(
      await subtle().decrypt({ name: "AES-GCM", iv: desc.nonce, additionalData: new TextEncoder().encode(desc.aad), tagLength: 128 }, gcmKey, sealed),
    );
  } catch {
    throw new Kcm1Error("chunk");
  }
  if ((await sha256Hex(plaintext)) !== desc.contentSha256) throw new Kcm1Error("digest");
  return plaintext;
}
